import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import data from "./data.js";
import "./PrintStyles.css";
import SEO from "../../components/SEO.jsx";

const colors = {
  navy: "#0F172A",
  blue: "#2563EB",
};

const Bar = ({ value, target, max, goodDirection = "down" }) => {
  const percent = Math.min(100, (value / max) * 100);
  const pass = goodDirection === "down" ? value <= target : value >= target;

  return (
    <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
      <div
        className={`h-4 ${pass ? "bg-green-500" : "bg-orange-400"}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

const ScoreCard = ({ title, primary, grade, note, color }) => (
  <div className="rounded-lg p-4 shadow-sm border border-slate-100 bg-slate-50">
    <p className="text-slate-600 text-sm mb-1 font-semibold">{title}</p>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-2xl font-bold">
        {primary !== undefined && primary !== null ? primary : "—"}
      </span>
      {grade && (
        <span className={`text-xs text-white px-2 py-0.5 rounded ${color}`}>
          {grade}
        </span>
      )}
    </div>
    {note && <p className="text-xs text-slate-500">{note}</p>}
  </div>
);

const VitalRow = ({ label, value, target, unit, goodDirection, max }) => (
  <div className="mb-4">
    <div className="flex justify-between text-sm mb-1">
      <span className="font-medium">
        {label} {value}
        {unit && ` ${unit}`}
      </span>
      <span className="text-slate-500">
        Target &lt; {target}
        {unit}
      </span>
    </div>
    <Bar value={value} target={target} max={max} goodDirection={goodDirection} />
  </div>
);

export default function HealthCheck() {
  const contentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: `Website Health Check - ${data.clientName}`,
    pageStyle: `
      @page { margin: 12mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print\\:hidden { display: none !important; }
        .shadow, .shadow-sm, .shadow-md, .shadow-lg { box-shadow: none !important; }
        section, header { break-inside: avoid; page-break-inside: avoid; }
      }
    `,
  });

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800 pt-24">
      <SEO
        title="Website Health Check | 2merative"
        description="Run a free website health check with 2merative. Diagnose performance, SEO, accessibility, and ROI issues that may be holding your business back."
        path="/website-health-check"
        image="/og-image.png"
      />

      <div ref={contentRef} className="container mx-auto max-w-4xl px-4 py-8">
        {/* HEADER */}
        <header
          className="rounded-xl text-white p-6 mb-6"
          style={{ backgroundColor: colors.navy }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <h1 className="text-2xl font-bold">
                2<span className="text-blue-400">merative</span>
              </h1>
              <p className="text-sm opacity-80">ITERATE. CREATE. ELEVATE.</p>
            </div>

            <div className="text-right">
              <h2 className="text-2xl font-semibold">Website Health Check</h2>
              <p className="opacity-80">
                {data.clientName ? `[${data.clientName}]` : "[Client Name]"}
              </p>
              <p className="text-sm opacity-80">
                {data.date} &nbsp; | &nbsp; Audited by: 2merative.com
              </p>
              <p className="text-sm opacity-80">URL: {data.url}</p>
            </div>
          </div>
        </header>

        {/* OVERVIEW SECTION */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">
            What is a Website Health Check?
          </h3>
          <p className="text-slate-700 text-sm mb-2">
            A Website Health Check is like a digital diagnostic for your online
            presence. It uncovers technical issues, slow loading speeds, mobile
            usability problems, SEO gaps, and accessibility issues that might be
            stopping your website from performing at its best.
          </p>
          <p className="text-slate-700 text-sm mb-2">
            By identifying and fixing these issues, we make your website faster,
            easier to use, more Google-friendly, and better at converting
            visitors into customers.
          </p>
          <p className="text-slate-700 text-sm">
            It’s not just about fixing bugs, it’s about elevating your online
            performance and getting more value from your website.
          </p>
        </section>

        {/* WHY THIS MATTERS */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-3 text-slate-900">
            Why you need a health check
          </h3>
          <ul className="list-disc list-inside space-y-1 text-slate-700">
            {data.whyItMatters.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>

        {/* EXECUTIVE SCORECARD */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Executive Scorecard
          </h3>

          <div className="grid md:grid-cols-3 gap-4">
            <ScoreCard
              title="Performance"
              primary={data.executive.performance.score}
              grade={data.executive.performance.grade}
              note={data.executive.performance.note}
              color="bg-green-600"
            />
            <ScoreCard
              title="SEO"
              primary={data.executive.seo.score}
              grade={data.executive.seo.grade}
              note={data.executive.seo.note}
              color="bg-orange-500"
            />
            <ScoreCard
              title="Accessibility"
              primary={data.executive.accessibility.score}
              grade={data.executive.accessibility.grade}
              note={data.executive.accessibility.note}
              color="bg-yellow-500"
            />
            <ScoreCard
              title="Best Practices"
              primary={data.executive.bestPractices.score}
              grade={data.executive.bestPractices.grade}
              note={data.executive.bestPractices.note}
              color="bg-blue-500"
            />
            <ScoreCard
              title="Mobile UX"
              primary={data.executive.mobile.grade}
              grade={data.executive.mobile.grade}
              note={data.executive.mobile.note}
              color="bg-orange-500"
            />
            <ScoreCard
              title="Security"
              primary={data.executive.security.grade}
              grade={data.executive.security.grade}
              note={data.executive.security.note}
              color="bg-yellow-500"
            />
            <ScoreCard
              title="Core Web Vitals"
              primary={data.executive.webVitals.passRate}
              grade=""
              note={data.executive.webVitals.note}
              color="bg-blue-500"
            />
          </div>
        </section>

        {/* CORE WEB VITALS SNAPSHOT */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Core Web Vitals Snapshot
          </h3>
          <VitalRow
            label="LCP"
            value={data.vitals.lcp.value}
            target={data.vitals.lcp.target}
            unit={data.vitals.lcp.unit}
            goodDirection="down"
            max={6}
          />
          <VitalRow
            label="INP"
            value={data.vitals.inp.value}
            target={data.vitals.inp.target}
            unit={data.vitals.inp.unit}
            goodDirection="down"
            max={600}
          />
          <VitalRow
            label="CLS"
            value={data.vitals.cls.value}
            target={data.vitals.cls.target}
            unit={data.vitals.cls.unit}
            goodDirection="down"
            max={0.3}
          />
        </section>

        {/* QUICK WINS + SEVERITY */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Top 5 Quick Wins
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">Priority</th>
                <th className="py-2">Task</th>
                <th className="py-2">Impact</th>
              </tr>
            </thead>
            <tbody>
              {data.quickWins.map((w, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2">{w.priority}</td>
                  <td className="py-2">{w.task}</td>
                  <td className="py-2">{w.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="text-lg font-semibold text-slate-900 mb-4 mt-6">
            Issue Breakdown by Severity
          </h3>
          <ul className="list-disc list-inside text-slate-700">
            <li>Critical: {data.severity.critical}</li>
            <li>High: {data.severity.high}</li>
            <li>Medium: {data.severity.medium}</li>
            <li>Low: {data.severity.low}</li>
          </ul>
        </section>

        {/* ROI SNAPSHOT */}
        <section className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            ROI Snapshot
          </h3>
          <ul className="list-disc list-inside space-y-1 text-slate-700">
            {data.roi.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>

        {/* CTA SECTION */}
        <section className="bg-slate-900 text-white rounded-lg shadow p-6 text-center print:hidden">
          <h3 className="text-xl font-semibold mb-2">
            Want these issues fixed?
          </h3>
          <p className="text-sm opacity-80 mb-4 max-w-xl mx-auto">
            This report shows what may be holding your website back. I can help
            fix the key issues and turn your site into a clearer, faster, more
            effective lead generator.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/contact"
              className="bg-blue-600 hover:bg-blue-700 px-5 py-2 rounded text-white font-medium transition"
            >
              Book a Call
            </a>

            <a
              href="/website-landing"
              className="bg-white text-slate-900 px-5 py-2 rounded font-medium hover:bg-gray-100 transition"
            >
              View How It Works
            </a>
          </div>
        </section>
      </div>

      {/* PRINT BUTTON */}
      <div className="text-center mt-6 print:hidden">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
}